const KEY_ACTIVE = "auth";
const KEY_PARKED = "auth.profiles";

function read(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (err) {
    return null;
  }
}

/**
 * The browser's two session slots, split by role rather than by identity.
 *
 *   auth           — THE ACTIVE SESSION, whoever that currently is.
 *   auth.profiles  — the sessions parked behind it, each with its own credentials.
 *
 * Because "the active session" is exactly what `auth` has always meant, nothing anywhere reads a
 * precedence rule: appendAuthorizationToken still reads auth.accessToken and is not modified at all,
 * and switching identity is a swap rather than an overlay. Migration is free — an existing `auth` is
 * already a valid active session and a missing `auth.profiles` reads as "nothing parked".
 *
 * ACTING IS DERIVED, NEVER STORED: a session is an acting session exactly when it has no refresh
 * token. There is no flag to fall out of sync, and the rule stays true for any future non-renewable
 * credential.
 *
 * THE ORDERING INVARIANT, IN BOTH DIRECTIONS: a session is never absent from both keys at once.
 * Parking appends to the park FIRST and writes the active session second; restoring writes the
 * active session FIRST and drops it from the park second. Either interruption leaves the same
 * recoverable state — one session present twice — which readParked() heals by dropping any entry
 * that matches the active session. A lost session is not recoverable; a duplicate is.
 */
export default class Session {
  static KEY_ACTIVE = KEY_ACTIVE;
  static KEY_PARKED = KEY_PARKED;

  static readActive() {
    return read(KEY_ACTIVE) || null;
  }

  static writeActive(session) {
    localStorage.setItem(KEY_ACTIVE, JSON.stringify(session || null));
  }

  static readParked() {
    const parked = read(KEY_PARKED);
    if ( !Array.isArray(parked) ) {
      return [];
    }
    const active = Session.readActive();
    const activeUserId = active ? active.user_id : null;
    return parked.filter(session => session && (!activeUserId || session.user_id !== activeUserId));
  }

  static writeParked(sessions) {
    localStorage.setItem(KEY_PARKED, JSON.stringify(sessions || []));
  }

  static park(current, next) {
    Session.writeParked([...Session.readParked(), current]);
    Session.writeActive(next);
  }

  static restore() {
    const parked = Session.readParked();
    if ( 0 === parked.length ) {
      return null;
    }
    const restored = parked[parked.length - 1];
    Session.writeActive(restored);
    Session.writeParked(parked.slice(0, -1));
    return restored;
  }

  static clearAll() {
    Session.writeActive(null);
    Session.writeParked([]);
  }

  static isRenewable(session) {
    return !!(session && session.refreshToken);
  }

  static isActing(session) {
    return !!session && !Session.isRenewable(session);
  }

  static isExpired(session, nowMs) {
    if ( !session || null === session.expiresAt || undefined === session.expiresAt ) {
      return false;
    }
    return nowMs >= session.expiresAt;
  }
}
